/**
 * A commission plan (`/commission-plans/:id`, TMMRSL-0049, TMMAPI-0104) — an object page with
 * an edit mode for drafts, built as the rate card page is (TMMRSL-0041). The header carries the
 * plan's name, status and its actions by state (Activate a draft; Copy and Close an active plan;
 * Copy to fix a non-compliant one; Copy a closed one). A DRAFT is edited in place (OWNER or ADMIN)
 * in a Simple or an Advanced form with an explicit Save (FRM-009), the adopted price card's plan
 * maximums shown live (its `controls`, TMMAPI-0154), "At a glance" and the warnings beside it, and
 * tmm-api's 24-month example below, on a rate card picked in the URL (`?card=`, replaced; a card
 * the preview says is gone reloads the cards, and the picker falls back to its default). An active
 * plan is frozen, so changing it means copying it. Every command reloads from the server; nothing
 * is optimistic. A draft save sends `version` when the plan has one (TMMAPI-0112). A plan read with the
 * TMMAPI-0105 extras (TMMRSL-0051) gets "Bonuses and caps", sends them back, and warns while a
 * bounty or a milestone bonus is in use: they aren't limited by the reseller's margin.
 */
import { useCallback, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { commissionPlansApi, type CommissionPlan } from '@/api/commissionPlans';
import { ApiError, backendCode } from '@/api/errors';
import { fieldErrorsOf } from '@/api/fieldErrors';
import { rateCardsApi, type AffiliateMaximums, type GrantedCard } from '@/api/rateCards';
import { useCanWrite } from '@/auth/authHooks';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LiveRenameDialog } from '@/components/LiveRenameDialog';
import { PageBreadcrumbs, PageHeader, type Breadcrumb } from '@/components/PageHeader';
import { SubmitButton } from '@/components/SubmitButton';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useRemote, type RemoteState } from '@/components/useRemote';
import { useToast } from '@/components/useToast';
import { useUnsavedGuard } from '@/components/useUnsavedGuard';
import { stringParam, useUrlState } from '@/components/useUrlState';
import { ViewOnlyTooltip } from '@/components/ViewOnly';
import { NoncompliantChip, StatusChip } from '@/components/status/StatusChip';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { NotFound } from '@/routes/NotFound';
import { Icon } from '@/theme';
import { AdoptCardLink } from './AdoptCardLink';
import { commissionPlanErrorMessage, isNoRateCard } from './commissionPlanErrors';
import { CommissionPlanForm, type FieldRefs } from './CommissionPlanForm';
import { exampleCardChoices, pickExampleCard } from './exampleCards';
import {
  FIELD_ORDER,
  fieldValue,
  formToBody,
  isDirty,
  planToBody,
  planToForm,
  reformatForm,
  serverFieldFor,
  type FormErrors,
  type FormField,
  type PlanForm,
} from './planForm';
import { PlanExample } from './PlanExample';
import { PlanSummaryPanel } from './PlanSummaryPanel';
import { useCopyPlan } from './useCopyPlan';
import { usePlanPreview } from './usePlanPreview';

/** A marked field still blank after a language switch keeps its mark (TMMRSL-0056). */
function keptFormatErrors(prior: FormErrors, next: FormErrors, form: PlanForm): FormErrors {
  const kept: FormErrors = {};
  for (const field of Object.keys(prior) as FormField[]) {
    const error = prior[field];
    if (!error || fieldValue(form, field).trim() !== '') continue;
    kept[field] = error;
  }
  return { ...kept, ...next };
}

const loadCards = () => rateCardsApi.listGrantedCards();

/** The example's rate card (`?card=`); a pick that isn't a choice falls back to the default. */
const CARD_SCHEMA = { card: stringParam() };

type ServerErrors = Partial<Record<FormField, true>>;

/** The form fields a server field map names, in the form's mode; unknown keys dropped. */
function serverErrorsOf(fields: Record<string, string>, mode: PlanForm['mode']): ServerErrors {
  const out: ServerErrors = {};
  for (const key of Object.keys(fields)) {
    const field = serverFieldFor(key, mode);
    if (field) out[field] = true;
  }
  return out;
}

function firstErrored(fields: Iterable<FormField>): FormField | undefined {
  const set = new Set(fields);
  return FIELD_ORDER.find((field) => set.has(field));
}

function isValidationFailed(err: unknown): boolean {
  return backendCode(err) === 'VALIDATION_FAILED';
}

/** A save failure shown with a message of the page's own, carrying the request id to quote. */
function saveFailure(message: string, err: unknown): ActionError {
  return {
    message,
    fieldKeys: [],
    code: backendCode(err),
    requestId: err instanceof ApiError ? err.requestId : undefined,
  };
}

interface LocationState {
  listSearch?: unknown;
}

export function CommissionPlanScreen() {
  const { id } = useParams();
  if (!id || !/^\d+$/.test(id)) return <NotFound />;
  // Keyed: another plan (after a copy) starts from a clean page.
  return <CommissionPlanPage key={id} id={Number(id)} />;
}

function CommissionPlanPage({ id }: { id: number }) {
  const { t } = useTranslation();
  const location = useLocation();
  const state = (location.state ?? null) as LocationState | null;
  const listSearch = typeof state?.listSearch === 'string' ? state.listSearch : '';

  const loadPlan = useCallback(() => commissionPlansApi.getPlan(id), [id]);
  const remote = useRemote(loadPlan);
  const cards = useRemote(loadCards);
  // A save answers the saved plan; it stands until the next load.
  const [saved, setSaved] = useState<{
    from: CommissionPlan | null;
    plan: CommissionPlan;
  } | null>(null);
  const plan = saved && saved.from === remote.data ? saved.plan : remote.data;

  // The breadcrumb's parent restores the list's filter (TMM-B7); it replaces the Back button.
  const parent: Breadcrumb = {
    label: t('pages.commissionPlans.title'),
    to: `/commission-plans${listSearch}`,
  };

  if (
    remote.status === 'error' &&
    remote.error instanceof ApiError &&
    remote.error.code === 'NOT_FOUND'
  ) {
    return (
      <Box>
        <PageHeader
          id="commissionPlan"
          title={t('commissionPlans.notFound')}
          subtitle=""
          breadcrumbs={[parent, { label: t('commissionPlans.notFound') }]}
        />
      </Box>
    );
  }

  return (
    <Box>
      {!plan ? (
        <PageBreadcrumbs items={[parent, { label: t('pages.commissionPlan.kicker') }]} />
      ) : null}
      {remote.status === 'error' ? (
        <ApiErrorState what={t('errors.what.commissionPlan')} error={remote.error} onRetry={remote.reload} />
      ) : null}
      {remote.status === 'loading' && !plan ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={220} />
        </Box>
      ) : null}
      {/* A failed reload keeps the last plan, so typed edits stay until a new one arrives. */}
      {plan ? (
        <PlanEditor
          plan={plan}
          onSaved={(next) => setSaved({ from: remote.data, plan: next })}
          reloadPlan={remote.reload}
          limits={cards.data?.find((card) => card.adopted)?.controls?.affiliate ?? null}
          cards={cards}
          parent={parent}
        />
      ) : null}
    </Box>
  );
}

interface PlanEditorProps {
  plan: CommissionPlan;
  /** The breadcrumb back to the list, with its filter. */
  parent: Breadcrumb;
  onSaved: (plan: CommissionPlan) => void;
  reloadPlan: () => void;
  /** The adopted card's plan maximums; `null` without an adopted card (tmm-api still checks). */
  limits: AffiliateMaximums | null;
  cards: RemoteState<GrantedCard[]>;
}

function PlanEditor({ plan, onSaved, reloadPlan, limits, cards, parent }: PlanEditorProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const canWrite = useCanWrite();
  const showToast = useToast();
  const describe = useActionError(commissionPlanErrorMessage);
  const { copy, copying } = useCopyPlan();
  const url = useUrlState(CARD_SCHEMA);

  const [renaming, setRenaming] = useState(false);
  const canRename = canWrite && plan.status !== 'DRAFT' && plan.editableFields.includes('name');
  const editable = plan.status === 'DRAFT' && canWrite;

  // Extras are always on the plan (TMMAPI-0164); SIMPLE still clears them in the write body.
  const extrasOptions = { extras: true, currency: plan.currency };

  // The form, reset from every plan load (and every save's answer). Its strings are in the locale
  // they were formatted in. On a language switch a clean form re-seeds from the plan; a dirty one
  // is re-formatted field by field (TMMRSL-0054). A field that doesn't parse is cleared and marked,
  // never kept as typed. Touched fields, save errors and dirtiness stay.
  const [formPlan, setFormPlan] = useState(plan);
  const [formLocale, setFormLocale] = useState(locale);
  const [form, setForm] = useState<PlanForm>(() => planToForm(plan, locale));
  const [touched, setTouched] = useState<ReadonlySet<FormField>>(() => new Set());
  const [submitErrors, setSubmitErrors] = useState<ServerErrors>({});
  const [formatErrors, setFormatErrors] = useState<FormErrors>({});
  const [nameTaken, setNameTaken] = useState(false);
  if (formPlan !== plan) {
    setFormPlan(plan);
    setFormLocale(locale);
    setForm(planToForm(plan, locale));
    setTouched(new Set());
    setSubmitErrors({});
    setFormatErrors({});
    setNameTaken(false);
  } else if (formLocale !== locale) {
    setFormLocale(locale);
    if (!isDirty(form, plan, formLocale)) {
      setForm(planToForm(plan, locale));
      setTouched(new Set());
      setSubmitErrors({});
      setFormatErrors({});
      setNameTaken(false);
    } else {
      const reformatted = reformatForm(form, formLocale, locale, extrasOptions);
      const merged = keptFormatErrors(formatErrors, reformatted.errors, reformatted.form);
      setForm(reformatted.form);
      setFormatErrors(merged);
      const marked = Object.keys(merged) as FormField[];
      if (marked.length > 0) setTouched(new Set([...touched, ...marked]));
    }
  }

  const fieldRefs: FieldRefs = useRef({});
  const pendingFocus = useRef<FormField | null>(null);
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ActionError | null>(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [dialog, setDialog] = useState<'activate' | 'close' | null>(null);

  // The example's rate card: every card that isn't closed, the pick in the URL.
  const choices = cards.data ? exampleCardChoices(cards.data, locale) : [];
  const exampleCard = pickExampleCard(choices, url.values.card || null);

  const dirty = editable && isDirty(form, plan, locale);
  const parsed = formToBody(form, locale, extrasOptions);
  // A field cleared on a language switch keeps the error of the text that failed, and blocks the
  // body, until the user changes it — an empty optional would otherwise parse as none.
  const client = {
    body: parsed.body !== null && Object.keys(formatErrors).length === 0 ? parsed.body : null,
    errors: { ...parsed.errors, ...formatErrors },
  };
  const preview = usePlanPreview(
    editable ? client.body : planToBody(plan),
    exampleCard?.id ?? null,
    // A card that's gone: the reloaded list no longer has it, so the pick falls back to the default.
    cards.reload,
  );
  // The last preview's marks describe the body it previewed; while a client error blocks the body
  // there is none, so only client and submit errors mark fields (UI-010).
  const previewErrors =
    editable && client.body === null
      ? {}
      : preview.preview
        ? serverErrorsOf(preview.preview.fields, form.mode)
        : {};
  const outOfLimits = Object.keys(previewErrors).length;
  const serverErrors: ServerErrors = { ...previewErrors, ...submitErrors };

  const guard = useUnsavedGuard({ when: dirty, name: plan.name });

  function pickCard(cardId: number) {
    // Replaced, keeping the location state (`listSearch` for the breadcrumb).
    url.set({ card: String(cardId) });
  }

  function focusField(field: FormField | undefined) {
    if (field) fieldRefs.current[field]?.focus();
  }

  function change(next: PlanForm) {
    const changed = (field: FormField) => fieldValue(next, field) !== fieldValue(form, field);
    // A value the user changes after a failed save is no longer the server's complaint.
    const stale = (Object.keys(submitErrors) as FormField[]).filter(changed);
    if (nameTaken && changed('name')) setNameTaken(false);
    if (stale.length > 0 || next.mode !== form.mode) {
      setSubmitErrors((prev) => {
        // A mode switch re-maps every field: the old marks name fields that aren't shown.
        if (next.mode !== form.mode) return {};
        const out = { ...prev };
        for (const field of stale) delete out[field];
        return out;
      });
    }
    const staleFormat = (Object.keys(formatErrors) as FormField[]).filter(changed);
    if (staleFormat.length > 0 || next.mode !== form.mode) {
      setFormatErrors((prev) => {
        if (next.mode !== form.mode) return {};
        const out = { ...prev };
        for (const field of staleFormat) delete out[field];
        return out;
      });
    }
    setForm(next);
  }

  function blur(field: FormField) {
    if (!touched.has(field)) setTouched(new Set(touched).add(field));
  }

  function discard() {
    setForm(planToForm(plan, locale));
    setTouched(new Set());
    setSubmitErrors({});
    setFormatErrors({});
    setNameTaken(false);
    setSaveError(null);
    setVersionConflict(false);
  }

  function markServerErrors(err: unknown): FormField | undefined {
    const named = serverErrorsOf(fieldErrorsOf(err), form.mode);
    setSubmitErrors(named);
    setTouched(new Set(FIELD_ORDER));
    return firstErrored(Object.keys(named) as FormField[]);
  }

  async function save() {
    if (!dirty || savingRef.current) return;
    if (client.body === null) {
      setTouched(new Set(FIELD_ORDER));
      focusField(firstErrored(Object.keys(client.errors) as FormField[]));
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    setVersionConflict(false);
    try {
      const body = { ...client.body, version: plan.version };
      const next = await commissionPlansApi.patchPlan(plan.id, body);
      showToast(t('commissionPlans.saved'));
      onSaved(next);
    } catch (err) {
      if (backendCode(err) === 'COMMISSION_PLAN_VERSION_CONFLICT') {
        setVersionConflict(true);
        setSaveError(saveFailure(t('commissionPlans.error.versionConflict'), err));
      } else if (backendCode(err) === 'COMMISSION_PLAN_NAME_TAKEN') {
        const message = commissionPlanErrorMessage(err, t) ?? t('commissionPlans.error.nameTaken');
        setNameTaken(true);
        setSubmitErrors({ name: true });
        setSaveError(saveFailure(message, err));
        focusField('name');
      } else if (isValidationFailed(err)) {
        setNameTaken(false);
        const first = markServerErrors(err);
        setSaveError(saveFailure(t('commissionPlans.error.validation'), err));
        focusField(first);
      } else {
        setNameTaken(false);
        setSaveError(describe(err));
        // Another member activated the draft meanwhile: show it as it now is.
        if (backendCode(err) === 'COMMISSION_PLAN_IMMUTABLE') reloadPlan();
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  async function activate() {
    try {
      await commissionPlansApi.activatePlan(plan.id);
    } catch (err) {
      if (isValidationFailed(err)) pendingFocus.current = markServerErrors(err) ?? null;
      throw err;
    }
    showToast(t('commissionPlans.activated', { name: plan.name }));
    reloadPlan();
  }

  async function closePlan() {
    await commissionPlansApi.closePlan(plan.id);
    showToast(t('commissionPlans.closedToast', { name: plan.name }));
    reloadPlan();
  }

  function closeDialog() {
    setDialog(null);
    const field = pendingFocus.current;
    pendingFocus.current = null;
    // After the dialog has handed focus back to its opener.
    if (field) setTimeout(() => focusField(field), 0);
  }

  /** A header action, disabled for STAFF with the view-only explanation. */
  function gated(button: ReactElement, key: string) {
    return canWrite ? (
      <Box component="span" key={key} sx={{ display: 'inline-flex' }}>
        {button}
      </Box>
    ) : (
      <ViewOnlyTooltip key={key}>{button}</ViewOnlyTooltip>
    );
  }

  const copyButton = (variant: 'contained' | 'outlined', label: string) => (
    <Button
      variant={variant}
      onClick={() => void copy(plan)}
      loading={copying === plan.id}
      disabled={!canWrite}
    >
      {label}
    </Button>
  );
  const closeButton = (
    <Button
      variant="outlined"
      color="error"
      onClick={() => setDialog('close')}
      disabled={!canWrite}
    >
      {t('commissionPlans.action.close')}
    </Button>
  );

  let actions: ReactNode[] = [];
  if (plan.status === 'DRAFT') {
    const activateButton = (
      <Button
        variant="contained"
        onClick={() => setDialog('activate')}
        disabled={!canWrite || dirty}
      >
        {t('commissionPlans.action.activate')}
      </Button>
    );
    actions = [
      canWrite && dirty ? (
        <Tooltip key="activate" title={t('commissionPlans.saveFirst')}>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            {activateButton}
          </Box>
        </Tooltip>
      ) : (
        gated(activateButton, 'activate')
      ),
    ];
  } else if (plan.status === 'ACTIVE' && !plan.noncompliant) {
    actions = [
      gated(copyButton('outlined', t('commissionPlans.action.copy')), 'copy'),
      gated(closeButton, 'close'),
    ];
  } else if (plan.status === 'ACTIVE') {
    actions = [
      gated(copyButton('contained', t('commissionPlans.action.copyToFix')), 'copy'),
      gated(closeButton, 'close'),
    ];
  } else {
    actions = [gated(copyButton('contained', t('commissionPlans.action.copy')), 'copy')];
  }

  let created = plan.currency;
  try {
    created = t('commissionPlans.created', {
      currency: plan.currency,
      date: formatInstant(
        plan.createdAt,
        locale,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        {
          dateStyle: 'medium',
        },
      ),
    });
  } catch {
    // An unparseable `createdAt`: the currency alone.
  }

  const footer = editable ? (
    <>
      <ActionErrorAlert
        error={saveError}
        sx={{ mt: 2.5 }}
        action={
          versionConflict ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setVersionConflict(false);
                setSaveError(null);
                reloadPlan();
              }}
            >
              {t('common.reload')}
            </Button>
          ) : undefined
        }
      />
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        sx={{ mt: 2.5, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <SubmitButton type="submit" disabled={!dirty} loading={saving}>
          {t('commissionPlans.action.save')}
        </SubmitButton>
        <Button onClick={discard} disabled={!dirty || saving}>
          {t('commissionPlans.action.discard')}
        </Button>
        {dirty ? (
          <Chip
            size="small"
            variant="outlined"
            icon={<Icon name="edit" />}
            label={t('commissionPlans.unsaved')}
          />
        ) : null}
        {outOfLimits > 0 ? (
          <Typography variant="body2" color="error">
            {t('commissionPlans.outOfLimits', { count: outOfLimits })}
          </Typography>
        ) : null}
      </Stack>
      {dirty ? (
        <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 1 }}>
          {t('commissionPlans.saveFirst')}
        </Typography>
      ) : null}
    </>
  ) : null;

  return (
    <>
      <PageHeader
        id="commissionPlan"
        title={plan.name || t('commissionPlans.unnamed')}
        subtitle={created}
        breadcrumbs={[
          parent,
          { label: plan.name || t('commissionPlans.unnamed') },
        ]}
        actions={
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <StatusChip domain="rateCard" status={plan.status} />
            {plan.noncompliant ? <NoncompliantChip /> : null}
            {canRename ? (
              <Button variant="outlined" onClick={() => setRenaming(true)}>
                {t('common.rename')}
              </Button>
            ) : null}
            {actions}
          </Stack>
        }
      />

      <Stack spacing={2} sx={{ mb: 2.5 }}>
        {plan.status === 'ACTIVE' && plan.noncompliant ? (
          <Alert severity="warning">{t('commissionPlans.noncompliantAlert')}</Alert>
        ) : null}
        {plan.status === 'ACTIVE' && !plan.noncompliant ? (
          <Alert severity="info">{t('commissionPlans.frozen')}</Alert>
        ) : null}
        {plan.status === 'CLOSED' ? (
          <Alert severity="info">{t('commissionPlans.closedNote')}</Alert>
        ) : null}
        {plan.status === 'DRAFT' ? (
          <Alert severity="info">{t('commissionPlans.assignHint')}</Alert>
        ) : null}
        {!editable ? <ActionErrorAlert error={saveError} /> : null}
      </Stack>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <CommissionPlanForm
            editable={editable}
            limits={limits}
            showExtras
            currency={plan.currency}
            form={form}
            onChange={change}
            errors={client.errors}
            serverErrors={serverErrors}
            {...(nameTaken
              ? { serverErrorText: { name: t('commissionPlans.error.nameTaken') } }
              : {})}
            touched={touched}
            onBlur={blur}
            fieldRefs={fieldRefs}
            onSubmit={() => void save()}
            footer={footer}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Stack spacing={2.5} sx={{ position: { lg: 'sticky' }, top: { lg: theme.spacing(2) } }}>
            <PlanSummaryPanel state={preview} cards={cards} choices={choices} />
            <Alert severity="info" variant="outlined">
              {t('commissionPlans.warn.lineLimit')}
            </Alert>
            <Alert severity="info" variant="outlined">
              {t('commissionPlans.warn.annual')}
            </Alert>
            {form.bountyOn || form.milestones.length > 0 ? (
              <Alert severity="warning" variant="outlined">
                {t('commissionPlans.warn.bonuses')}
              </Alert>
            ) : null}
          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2.5 }}>
        <PlanExample
          state={preview}
          cards={cards}
          choices={choices}
          card={exampleCard}
          onPickCard={pickCard}
          hasMilestones={form.milestones.length > 0}
        />
      </Box>

      <ConfirmDialog
        open={dialog === 'activate'}
        title={t('commissionPlans.activateConfirm.title', { name: plan.name })}
        consequence={t('commissionPlans.activateConfirm.body')}
        confirmLabel={t('commissionPlans.action.activate')}
        onConfirm={activate}
        onCancel={closeDialog}
        errorMessage={(err) =>
          isValidationFailed(err) ? t('commissionPlans.error.validation') : describe(err).message
        }
        errorAction={(err) => (isNoRateCard(err) ? <AdoptCardLink /> : null)}
      />
      <ConfirmDialog
        open={dialog === 'close'}
        title={t('commissionPlans.closeConfirm.title', { name: plan.name })}
        consequence={t('commissionPlans.closeConfirm.body')}
        confirmLabel={t('commissionPlans.action.close')}
        destructive
        onConfirm={closePlan}
        onCancel={closeDialog}
        errorMessage={(err) => describe(err).message}
      />
      {renaming ? (
        <LiveRenameDialog
          name={plan.name}
          onSave={(name) =>
            commissionPlansApi.patchPlan(plan.id, { name, version: plan.version })
          }
          onClose={() => setRenaming(false)}
          onReload={reloadPlan}
        />
      ) : null}
      {guard}
    </>
  );
}
